import { motion } from "framer-motion";
import {
  ArrowLeft,
  Calendar,
  CreditCard,
  FileText,
  Home,
  Mail,
  Phone,
  User,
  Users,
  Shirt,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api from "../services/api";

const RegistrationDetailsPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [registration, setRegistration] = useState(null);
  const [additionalRegistration, setAdditionalRegistration] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [addParticipants, setAddParticipants] = useState([]);

  useEffect(() => {
    const fetchRegistrationDetails = async () => {
      try {
        setLoading(true);
        
        // Fetch main registration
        const data = await api.registration.getById(id);
        setRegistration(data);

        // Fetch all additional registrations
        const addData = await api.registration.getAllAddRegistrations();
        setAddParticipants(addData);

        // Find additional registration for this mobile number
        if (data && data.mobile) {
          const matchingAdd = addData.find(addP => addP.mobile === data.mobile);
          if (matchingAdd) {
            setAdditionalRegistration(matchingAdd);
          }
        }

      } catch (error) {
        console.error("Error fetching registration details:", error);
        setError("Failed to load registration details");
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchRegistrationDetails();
    }
  }, [id]);

  const handleBack = () => {
    navigate(-1);
  };

  const handleHome = () => {
    navigate("/home");
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getPaymentStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "success":
      case "paid":
        return "bg-green-500 text-white";
      case "pending":
        return "bg-yellow-500 text-white";
      case "failed":
      case "cancelled":
        return "bg-red-500 text-white";
      default:
        return "bg-gray-500 text-white";
    }
  };

  const calculateTotalAmount = () => {
    let total = registration?.totalAmount || 0;
    if (additionalRegistration) {
      total += additionalRegistration.totalAmount || 0;
    }
    return total;
  };

  // Get the appropriate accompany data (additional if exists, otherwise main)
  const getDisplayAccompany = () => {
    if (additionalRegistration) {
      return additionalRegistration.accompany || {
        spouse: 0,
        guest: 0,
        driver: 0,
        childAbove5: 0,
        childBelow5: 0,
      };
    }
    return registration?.accompany || {
      spouse: 0,
      guest: 0,
      driver: 0,
      childAbove5: 0,
      childBelow5: 0,
    };
  };

  // Get the appropriate child T-shirt sizes (additional if exists, otherwise main)
  const getDisplayChildTShirtSizes = () => {
    if (additionalRegistration) {
      return {
        above5: additionalRegistration.childAbove5TshirtSize,
        below5: additionalRegistration.childBelow5TshirtSize,
      };
    }
    return {
      above5: registration?.childAbove5TshirtSize,
      below5: registration?.childBelow5TshirtSize,
    };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-indigo-100 to-purple-100 flex items-center justify-center">
        <div className="text-center">
          <div className="spinner-border text-green-600" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3 text-gray-700">Loading registration details...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-indigo-100 to-purple-100 flex items-center justify-center">
        <div className="text-center text-gray-800">
          <h2 className="text-2xl font-bold mb-4">Error</h2>
          <p className="mb-4">{error}</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  if (!registration) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-100 via-indigo-100 to-purple-100 flex items-center justify-center">
        <div className="text-center text-gray-800">
          <h2 className="text-2xl font-bold mb-4">Registration Not Found</h2>
          <p className="mb-4">The requested registration could not be found.</p>
          <button
            onClick={handleBack}
            className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const displayAccompany = getDisplayAccompany();
  const displayChildTShirtSizes = getDisplayChildTShirtSizes();

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-100 via-indigo-100 to-purple-100">
      {/* Header */}
      <div className="container mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8"
        >
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-green-800 mb-2">
              Registration Details
            </h1>
            <p className="text-green-600">
              KGC'97 Day 2026 - {registration.batch} Batch
            </p>
          </div>

          <div className="flex gap-3 mt-4 md:mt-0">
            <button
              onClick={handleBack}
              className="px-4 py-2 bg-green-500 hover:bg-green-600 text-white rounded-lg flex items-center gap-2"
            >
              <ArrowLeft size={18} />
              Back
            </button>
            <button
              onClick={handleHome}
              className="px-4 py-2 bg-yellow-500 hover:bg-yellow-600 text-white rounded-lg flex items-center gap-2"
            >
              <Home size={18} />
              Home
            </button>
          </div>
        </motion.div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Participant Info */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="lg:col-span-2"
          >
            <div className="bg-white rounded-2xl shadow-xl p-6 mb-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="bg-gradient-to-br from-green-500 to-green-600 w-12 h-12 rounded-xl flex items-center justify-center">
                  <User className="text-white" size={24} />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-800">
                    {registration.name}
                  </h2>
                  {additionalRegistration && (
                    <span className="inline-block mt-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
                      + Additional Payment Available
                    </span>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Personal Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                    <User size={18} />
                    Personal Information
                  </h3>

                  <div className="space-y-3">
                    <div className="min-w-0">
                      <p className="text-sm text-gray-500 mb-1">Email</p>
                      <div className="text-gray-800 flex items-start gap-2 min-w-0">
                        <Mail size={16} className="flex-shrink-0 mt-0.5" />
                        <span className="break-all whitespace-normal flex-1">
                          {registration.email || "N/A"}
                        </span>
                      </div>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Mobile</p>
                      <p className="text-gray-800 flex items-center gap-2">
                        <Phone size={16} />
                        {registration.mobile}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Batch</p>
                      <span className="inline-block px-3 py-1 bg-green-100 text-green-700 rounded-full">
                        {registration.batch}
                      </span>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">T-Shirt Size</p>
                      <span className="inline-block px-3 py-1 bg-blue-100 text-blue-700 rounded-full">
                        {registration.tshirtSize || "N/A"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Registration Info */}
                <div className="space-y-4">
                  <h3 className="text-lg font-semibold text-gray-700 flex items-center gap-2">
                    <Calendar size={18} />
                    Registration Details
                  </h3>

                  <div className="space-y-3">
                    <div>
                      <p className="text-sm text-gray-500 mb-1">
                        Registration Date
                      </p>
                      <p className="text-gray-800">
                        {formatDate(registration.registrationDate)}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Order ID</p>
                      <p className="text-gray-800 font-mono text-sm">
                        {registration.orderId || "N/A"}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Transaction ID</p>
                      <p className="text-gray-800 font-mono text-sm">
                        {registration.transactionId || "N/A"}
                      </p>
                    </div>

                    <div>
                      <p className="text-sm text-gray-500 mb-1">Status</p>
                      <span
                        className={`inline-block px-3 py-1 rounded-full ${getPaymentStatusColor(
                          registration.paymentStatus
                        )}`}
                      >
                        {registration.paymentStatus || "Pending"}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* T-Shirt Sizes for Children */}
            {(displayChildTShirtSizes.above5 || displayChildTShirtSizes.below5) && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="bg-white rounded-2xl shadow-xl p-6"
              >
                <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-6">
                  <Shirt size={20} />
                  Children T-Shirt Sizes
                </h3>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {displayChildTShirtSizes.above5 && (
                    <div className="bg-green-50 rounded-xl p-4">
                      <p className="text-sm text-gray-600 mb-2">Children Above 5</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xl font-bold text-gray-800">
                          {displayChildTShirtSizes.above5}
                        </p>
                        <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
                          <Users className="text-white" size={20} />
                        </div>
                      </div>
                    </div>
                  )}

                  {displayChildTShirtSizes.below5 && (
                    <div className="bg-purple-50 rounded-xl p-4">
                      <p className="text-sm text-gray-600 mb-2">Children Below 5</p>
                      <div className="flex items-center justify-between">
                        <p className="text-xl font-bold text-gray-800">
                          {displayChildTShirtSizes.below5}
                        </p>
                        <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
                          <Users className="text-white" size={20} />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </motion.div>
            )}
          </motion.div>

          {/* Right Column - Payment & Accompanying */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
            className="space-y-6"
          >
            {/* Payment Summary */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-6">
                <CreditCard size={20} />
                Payment Summary
              </h3>

              {/* Main Registration Amount */}
              <div className="space-y-3 mb-4">
                <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                  <p className="text-gray-600">Main Registration</p>
                  <p className="text-lg font-semibold text-gray-800">
                    ৳{registration.totalAmount?.toLocaleString() || 0}
                  </p>
                </div>

                {/* Additional Registration Amount */}
                {additionalRegistration && (
                  <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                    <p className="text-gray-600 flex items-center gap-1">
                      Additional Payment
                    </p>
                    <p className="text-lg font-semibold text-purple-600">
                      + ৳{additionalRegistration.totalAmount?.toLocaleString() || 0}
                    </p>
                  </div>
                )}
              </div>

              {/* Total Amount */}
              <div className="flex justify-between items-center mt-4 pt-4 border-t-2 border-gray-300">
                <p className="text-xl font-bold text-gray-800">Total Amount</p>
                <p className="text-3xl font-bold text-green-600">
                  ৳{calculateTotalAmount().toLocaleString()}
                </p>
              </div>

              {/* Payment Method Details */}
              <div className="mt-6 space-y-3">
                <div>
                  <p className="text-sm text-gray-500 mb-1">Payment Method</p>
                  <p className="text-gray-800 font-medium">
                    {registration.paymentMethod || "Not specified"}
                  </p>
                </div>

                {registration.transactionId && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      Transaction ID
                    </p>
                    <p className="text-gray-800 font-mono text-sm bg-gray-50 p-2 rounded">
                      {registration.transactionId}
                    </p>
                  </div>
                )}

                {registration.bankTransactionId && (
                  <div>
                    <p className="text-sm text-gray-500 mb-1">
                      Bank Transaction ID
                    </p>
                    <p className="text-gray-800 font-mono text-sm bg-gray-50 p-2 rounded">
                      {registration.bankTransactionId}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Accompanying Persons */}
            <div className="bg-white rounded-2xl shadow-xl p-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2 mb-6">
                <Users size={20} />
                Accompanying Persons
              </h3>

              <div className="space-y-1">
                <div className="bg-green-50 rounded-xl px-4 py-0">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-700 font-semibold">Spouse</p>
                    <p className="text-2xl font-bold text-green-600">
                      {displayAccompany.spouse || 0}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-50 rounded-xl px-4 py-0">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-700 font-semibold">Guest</p>
                    <p className="text-2xl font-bold text-yellow-600">
                      {displayAccompany.guest || 0}
                    </p>
                  </div>
                </div>

                <div className="bg-red-50 rounded-xl px-4 py-0">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-700 font-semibold">Driver</p>
                    <p className="text-2xl font-bold text-red-600">
                      {displayAccompany.driver || 0}
                    </p>
                  </div>
                </div>

                <div className="bg-blue-50 rounded-xl px-4 py-0">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-700 font-semibold">
                      Children (Above 5)
                    </p>
                    <p className="text-2xl font-bold text-blue-600">
                      {displayAccompany.childAbove5 || 0}
                    </p>
                  </div>
                </div>

                <div className="bg-purple-50 rounded-xl px-4 py-0">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-700 font-semibold">
                      Children (Below 5)
                    </p>
                    <p className="text-2xl font-bold text-purple-600">
                      {displayAccompany.childBelow5 || 0}
                    </p>
                  </div>
                </div>

                {/* Total Accompanying Count */}
                <div className="mt-1 pt-1 border-t border-gray-200">
                  <div className="flex justify-between items-center">
                    <p className="text-gray-700 font-semibold">Total Accompanying</p>
                    <p className="text-xl font-bold text-gray-800">
                      {Object.values(displayAccompany).reduce((a, b) => a + b, 0)}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Additional Registration Details Card - Only shown when additional exists */}
        {additionalRegistration && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.35 }}
            className="mt-6 bg-purple-50 rounded-2xl shadow-xl p-6 border border-purple-200"
          >
            <h3 className="text-lg font-bold text-purple-800 flex items-center gap-2 mb-4">
              <CreditCard size={18} />
              Additional Payment Details
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <p className="text-sm text-purple-600">Amount</p>
                <p className="font-bold text-purple-800">
                  ৳{additionalRegistration.totalAmount?.toLocaleString()}
                </p>
              </div>
              <div>
                <p className="text-sm text-purple-600">Payment Method</p>
                <p className="text-purple-800">
                  {additionalRegistration.paymentMethod || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-purple-600">Transaction ID</p>
                <p className="text-purple-800 font-mono text-sm">
                  {additionalRegistration.transactionId || "N/A"}
                </p>
              </div>
              <div>
                <p className="text-sm text-purple-600">Date</p>
                <p className="text-purple-800">
                  {formatDate(additionalRegistration.registrationDate)}
                </p>
              </div>
            </div>
          </motion.div>
        )}

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-8 text-center text-gray-600 text-sm"
        >
          <p>
            For any issues regarding this registration, please contact the event
            organizers.
          </p>
          <p className="mt-1">
            Last updated: {formatDate(registration.registrationDate)}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default RegistrationDetailsPage;

// import { motion } from "framer-motion";
// import {
//   ArrowLeft,
//   Calendar,
//   CreditCard,
//   FileText,
//   Home,
//   Mail,
//   Phone,
//   User,
//   Users,
// } from "lucide-react";
// import React, { useEffect, useState } from "react";
// import { useNavigate, useParams } from "react-router-dom";
// import { useAuth } from "../context/AuthContext";
// import api from "../services/api";

// const RegistrationDetailsPage = () => {
//   const { id } = useParams();
//   const navigate = useNavigate();
//   const { user } = useAuth();
//   const [registration, setRegistration] = useState(null);
//   const [loading, setLoading] = useState(true);
//   const [error, setError] = useState(null);

//   useEffect(() => {
//     const fetchRegistrationDetails = async () => {
//       try {
//         setLoading(true);
//         const data = await api.registration.getById(id);

//         // Check if the registration belongs to the current user
//         // if (data.userId !== user?.id && user?.role !== "ADMIN") {
//         //   setError("Unauthorized access");
//         //   return;
//         // }

//         setRegistration(data);
//       } catch (error) {
//         console.error("Error fetching registration details:", error);
//         setError("Failed to load registration details");
//       } finally {
//         setLoading(false);
//       }
//     };

//     if (id) {
//       fetchRegistrationDetails();
//     }
//   }, [id]);

//   const handleBack = () => {
//     navigate(-1);
//   };

//   const handleHome = () => {
//     navigate("/home");
//   };

//   const isPaymentCompleted = (paymentStatus) => {
//     return paymentStatus === "Success" || paymentStatus === "Paid";
//   };

//   const formatDate = (dateString) => {
//     const date = new Date(dateString);
//     return date.toLocaleDateString("en-US", {
//       year: "numeric",
//       month: "long",
//       day: "numeric",
//     });
//   };

//   const getPaymentStatusColor = (status) => {
//     switch (status?.toLowerCase()) {
//       case "success":
//       case "paid":
//         return "bg-green-500 text-white";
//       case "pending":
//         return "bg-yellow-500 text-white";
//       case "failed":
//       case "cancelled":
//         return "bg-red-500 text-white";
//       default:
//         return "bg-gray-500 text-white";
//     }
//   };

//   if (loading) {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-800 to-purple-900 flex items-center justify-center">
//         <div className="text-center">
//           <div className="spinner-border text-white" role="status">
//             <span className="visually-hidden">Loading...</span>
//           </div>
//           <p className="mt-3 text-white">Loading registration details...</p>
//         </div>
//       </div>
//     );
//   }

//   if (error) {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-800 to-purple-900 flex items-center justify-center">
//         <div className="text-center text-white">
//           <h2 className="text-2xl font-bold mb-4">Error</h2>
//           <p className="mb-4">{error}</p>
//           <button
//             onClick={handleBack}
//             className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20"
//           >
//             Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   if (!registration) {
//     return (
//       <div className="min-h-screen bg-gradient-to-br from-purple-900 via-indigo-800 to-purple-900 flex items-center justify-center">
//         <div className="text-center text-white">
//           <h2 className="text-2xl font-bold mb-4">Registration Not Found</h2>
//           <p className="mb-4">The requested registration could not be found.</p>
//           <button
//             onClick={handleBack}
//             className="px-4 py-2 bg-white/10 hover:bg-white/20 text-white rounded-lg border border-white/20"
//           >
//             Go Back
//           </button>
//         </div>
//       </div>
//     );
//   }

//   return (
//     <div className="min-h-screen bg-gradient-to-br from-green-100 via-indigo-100 to-purple-100">
//       {/* Header */}
//       <div className="container mx-auto px-4 py-8">
//         <motion.div
//           initial={{ opacity: 0, y: -20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5 }}
//           className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8"
//         >
//           <div>
//             <h1 className="text-3xl md:text-4xl font-bold text-green mb-2">
//               Registration Details
//             </h1>
//             <p className="text-green-600">
//               KGC'97 Day 2026 - {registration.batch} Batch
//             </p>
//           </div>

//           <div className="flex gap-3 mt-4 md:mt-0">
        
//             <button
//               onClick={handleBack}
//               className="px-4 py-2 bg-green-400 hover:bg-green-500 text-white rounded-lg border border-green-400 flex items-center gap-2"
//             >
//               <ArrowLeft size={18} />
//               Back
//             </button>
//             <button
//               onClick={handleHome}
//               className="px-4 py-2 bg-yellow-400 hover:bg-yellow-500 text-white rounded-lg border border-yellow-400 flex items-center gap-2"
//             >
//               <Home size={18} />
//               Home
//             </button>
//           </div>
//         </motion.div>

//         {/* Main Content */}
//         <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
//           {/* Left Column - Participant Info */}
//           <motion.div
//             initial={{ opacity: 0, x: -20 }}
//             animate={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.5, delay: 0.1 }}
//             className="lg:col-span-2"
//           >
//             <div className="solid-bg rounded-2xl shadow-xl p-6 mb-6">
//               <div className="flex items-center gap-3 mb-6">
//                 <div className="bg-gradient-to-br from-blue-500 to-purple-500 w-12 h-12 rounded-xl flex items-center justify-center">
//                   <User className="text-white" size={24} />
//                 </div>
//                 <div>
//                   <h2 className="text-2xl font-bold text-white">
//                     {registration.name}
//                   </h2>
//                 </div>
//               </div>

//               <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
//                 {/* Personal Info */}
//                 <div className="space-y-4">
//                   <h3 className="text-lg font-semibold text-white flex items-center gap-2">
//                     <User size={18} />
//                     Personal Information
//                   </h3>

//                   <div className="space-y-3">
//                   <div className="min-w-0">
//         <p className="text-sm text-purple-300 mb-1">Email</p>
//         <div className="text-white flex items-start gap-2 min-w-0">
//           <Mail size={16} className="flex-shrink-0 mt-0.5" />
//           <span className="break-all whitespace-normal flex-1">
//             {registration.email}
//           </span>
//         </div>
//       </div>

//                     <div>
//                       <p className="text-sm text-purple-300 mb-1">Mobile</p>
//                       <p className="text-white flex items-center gap-2">
//                         <Phone size={16} />
//                         {registration.mobile}
//                       </p>
//                     </div>

//                     <div>
//                       <p className="text-sm text-purple-300 mb-1">Batch</p>
//                       <span className="inline-block px-3 py-1 bg-purple-600 text-white rounded-full">
//                         {registration.batch}
//                       </span>
//                     </div>
//                   </div>
//                 </div>

//                 {/* Registration Info */}
//                 <div className="space-y-4">
//                   <h3 className="text-lg font-semibold text-white flex items-center gap-2">
//                     <Calendar size={18} />
//                     Registration Details
//                   </h3>

//                   <div className="space-y-3">
//                     <div>
//                       <p className="text-sm text-purple-300 mb-1">
//                         Registration Date
//                       </p>
//                       <p className="text-white">
//                         {formatDate(registration.registrationDate)}
//                       </p>
//                     </div>

//                     <div>
//                       <p className="text-sm text-purple-300 mb-1">Order ID</p>
//                       <p className="text-white font-mono text-sm">
//                         {registration.orderId || "N/A"}
//                       </p>
//                     </div>

//                     <div>
//                       <p className="text-sm text-purple-300 mb-1">Status</p>
//                       <span
//                         className={`inline-block px-3 py-1 rounded-full ${getPaymentStatusColor(
//                           registration.paymentStatus
//                         )}`}
//                       >
//                         {registration.paymentStatus || "Pending"}
//                       </span>
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </div>

//             {/* T-Shirt Sizes */}
//             <motion.div
//               initial={{ opacity: 0, y: 20 }}
//               animate={{ opacity: 1, y: 0 }}
//               transition={{ duration: 0.5, delay: 0.2 }}
//               className="solid-bg rounded-2xl shadow-xl p-6"
//             >
//               <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
//                 <shirt size={20} />
//                 T-Shirt Sizes
//               </h3>

//               <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
//                 <div className="bg-white/5 rounded-xl p-4">
//                   <p className="text-sm text-purple-300 mb-2">Participant</p>
//                   <div className="flex items-center justify-between">
//                     <p className="text-xl font-bold text-white">
//                       {registration.tshirtSize || "N/A"}
//                     </p>
//                     <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg flex items-center justify-center">
//                       <shirt className="text-white" size={20} />
//                     </div>
//                   </div>
//                 </div>

//                 <div className="bg-white/5 rounded-xl p-4">
//                   <p className="text-sm text-purple-300 mb-2">
//                     Child (Above 5)
//                   </p>
//                   <div className="flex items-center justify-between">
//                     <p className="text-xl font-bold text-white">
//                       {registration.childAbove5TshirtSize || "N/A"}
//                     </p>
//                     <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-blue-500 rounded-lg flex items-center justify-center">
//                       <Users className="text-white" size={20} />
//                     </div>
//                   </div>
//                 </div>

//                 <div className="bg-white/5 rounded-xl p-4">
//                   <p className="text-sm text-purple-300 mb-2">
//                     Child (Below 5)
//                   </p>
//                   <div className="flex items-center justify-between">
//                     <p className="text-xl font-bold text-white">
//                       {registration.childBelow5TshirtSize || "N/A"}
//                     </p>
//                     <div className="w-10 h-10 bg-gradient-to-br from-pink-500 to-purple-500 rounded-lg flex items-center justify-center">
//                       <Users className="text-white" size={20} />
//                     </div>
//                   </div>
//                 </div>
//               </div>
//             </motion.div>
//           </motion.div>

//           {/* Right Column - Payment & Accompanying */}
//           <motion.div
//             initial={{ opacity: 0, x: 20 }}
//             animate={{ opacity: 1, x: 0 }}
//             transition={{ duration: 0.5, delay: 0.3 }}
//             className="space-y-6"
//           >
//             {/* Payment Summary */}
//             <div className="solid-bg rounded-2xl shadow-xl p-6">
//               <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
//                 <CreditCard size={20} />
//                 Payment Summary
//               </h3>

//               <div className="flex justify-between items-center">
//                 <p className="text-lg  text-white">Total Amount</p>
//                 <p className="text-2xl font-bold text-yellow-400">
//                   ৳{registration.totalAmount?.toLocaleString() || 0}
//                 </p>
//               </div>

//               <div className="mt-2 space-y-3">
//                 <div>
//                   <p className="text-sm text-purple-300 mb-1">Payment Method</p>
//                   <p className="text-white">
//                     {registration.paymentMethod || "Not specified"}
//                   </p>
//                 </div>

//                 {registration.transactionId && (
//                   <div>
//                     <p className="text-sm text-purple-300 mb-1">
//                       Transaction ID
//                     </p>
//                     <p className="text-white font-mono text-sm">
//                       {registration.transactionId}
//                     </p>
//                   </div>
//                 )}

//                 {registration.bankTransactionId && (
//                   <div>
//                     <p className="text-sm text-purple-300 mb-1">
//                       Bank Transaction ID
//                     </p>
//                     <p className="text-white font-mono text-sm">
//                       {registration.bankTransactionId}
//                     </p>
//                   </div>
//                 )}
//               </div>
//             </div>

//             {/* Accompanying Persons */}
//             <div className="solid-bg rounded-2xl shadow-xl p-6">
//               <h3 className="text-xl font-bold text-white flex items-center gap-2 mb-6">
//                 <Users size={20} />
//                 Accompanying Persons
//               </h3>

//               <div className="space-y-4">
//                 <div className="bg-white/5 rounded-xl px-4">
//                   <div className="flex justify-between items-center">
//                     <p className="text-white font-semibold">spouse</p>
//                     <p className="text-2xl font-bold text-white">
//                       {registration.accompany?.spouse || 0}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="bg-white/5 rounded-xl px-4">
//                   <div className="flex justify-between items-center">
//                     <p className="text-white font-semibold">
//                       Children (Above 5)
//                     </p>
//                     <p className="text-2xl font-bold text-white">
//                       {registration.accompany?.childAbove5 || 0}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="bg-white/5 rounded-xl px-4">
//                   <div className="flex justify-between items-center">
//                     <p className="text-white font-semibold">
//                       Children (Below 5)
//                     </p>
//                     <p className="text-2xl font-bold text-white">
//                       {registration.accompany?.childBelow5 || 0}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="bg-white/5 rounded-xl px-4">
//                   <div className="flex justify-between items-center">
//                     <p className="text-white font-semibold">
//                       Guest
//                     </p>
//                     <p className="text-2xl font-bold text-white">
//                       {registration.accompany?.guest || 0}
//                     </p>
//                   </div>
//                 </div>

//                 <div className="bg-white/5 rounded-xl px-4">
//                   <div className="flex justify-between items-center">
//                     <p className="text-white font-semibold">
//                       Driver
//                     </p>
//                     <p className="text-2xl font-bold text-white">
//                       {registration.accompany?.driver || 0}
//                     </p>
//                   </div>
//                 </div>
//               </div>
//             </div>

//           </motion.div>
//         </div>

//         {/* Footer Note */}
//         <motion.div
//           initial={{ opacity: 0, y: 20 }}
//           animate={{ opacity: 1, y: 0 }}
//           transition={{ duration: 0.5, delay: 0.4 }}
//           className="mt-8 text-center text-green-500 text-sm"
//         >
//           <p>
//             For any issues regarding this registration, please contact the event
//             organizers.
//           </p>
//           <p className="mt-1">
//             Last updated: {formatDate(registration.registrationDate)}
//           </p>
//         </motion.div>
//       </div>
//     </div>
//   );
// };

// export default RegistrationDetailsPage;
